/**
 * DomainBundleList — middle column of the smart inbox view.
 *
 * Renders a search/filter header and a scrollable list of DomainBundleItem
 * cards, one per domain. Auto-selects the first domain on mount and on
 * folder change.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Inbox as InboxIcon } from 'lucide-react';
import { FilterSearch, EmptyState } from '../../../ui';
import { DomainBundleItem } from './DomainBundleItem';
import { useInbox } from '../InboxContext';

const STATUS_FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'unread',  label: 'Unread' },
  { key: 'starred', label: 'Flagged' },
];

function DomainBundleList({ bundles }) {
  const { selectedDomain, selectDomain, activeFolder } = useInbox();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const prevFolder = useRef(activeFolder);

  // Filter bundles by search and status
  const filtered = useMemo(() => {
    let list = bundles;

    // Status filter — hide domains with no matching emails
    if (statusFilter === 'unread') {
      list = list.filter((b) => b.unreadCount > 0);
    } else if (statusFilter === 'starred') {
      // Check if any email in the domain is starred
      list = list.filter((b) =>
        b.topicGroups.some((g) =>
          g.emails.some((e) => e.isStarred)
        )
      );
    }

    // Search — match domain name or any email subject/from within
    if (search.trim()) {
      const terms = search.split('+').map((t) => t.trim().toLowerCase()).filter(Boolean);
      list = list.filter((b) => {
        const domainText = b.domainLabel.toLowerCase();
        const emailTexts = b.topicGroups.flatMap((g) =>
          g.emails.map((e) =>
            [e.from.name, e.subject, e.preview, e.primaryDomain].join(' ').toLowerCase()
          )
        );
        const allText = [domainText, ...emailTexts].join(' ');
        return terms.every((term) => allText.includes(term));
      });
    }

    return list;
  }, [bundles, search, statusFilter]);

  // Auto-select first domain on mount and folder change
  useEffect(() => {
    if (prevFolder.current !== activeFolder || !selectedDomain) {
      prevFolder.current = activeFolder;
      if (filtered.length > 0) {
        selectDomain(filtered[0].domainKey);
      }
    }
  }, [activeFolder, filtered, selectDomain, selectedDomain]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {/* Header — search + status filter */}
      <div style={{ flexShrink: 0, padding: 6, borderBottom: '1px solid var(--surface)' }}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          size="sm"
          placeholder="Search domains..."
        />
        <div className="flex items-center gap-1 mt-1.5">
          {STATUS_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className="text-body-sm px-2 py-0.5 rounded transition-colors"
              style={{
                fontWeight: statusFilter === key ? 600 : 400,
                color: statusFilter === key ? 'var(--aqua)' : 'var(--text-dark-secondary)',
                background: statusFilter === key ? 'var(--aqua-glow)' : 'transparent',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable bundle list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 6 }}>
        <div className="flex flex-col gap-1.5">
          {filtered.length > 0 ? (
            filtered.map((bundle) => (
              <DomainBundleItem
                key={bundle.domainKey}
                bundle={bundle}
                selected={selectedDomain === bundle.domainKey}
                onClick={() => selectDomain(bundle.domainKey)}
              />
            ))
          ) : (
            <EmptyState
              icon={<InboxIcon className="h-6 w-6" />}
              title="No domains"
              body="No email domains match your filters."
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );
}

export { DomainBundleList };
